import { GearIcon, XIcon } from "@phosphor-icons/react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/data-display/tooltip";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import { Menu, MenuPopover, MenuTrigger } from "@/components/ui/menu";
import { CustomMenuItem, type MenuItemConfig } from "@/components/ui/menu-item";
import { getPrettyName } from "@/lib/utils/prettyName";

interface GenericFilterPillProps {
  /** The category name (e.g., "PUBLISHER", "CATEGORY", "Topic") */
  category: string;
  /** Array of selected option values */
  selectedOptions: string[];
  /** Optional callback when clear button is clicked */
  onClear?: () => void;
  /** If true, always show count instead of option list, with tooltip showing all options */
  countOnly?: boolean;
  /** If true, shows loading skeleton instead of content */
  isLoading?: boolean;
  /** Array of menu items to display when gear icon is clicked */
  menuItems?: MenuItemConfig[];
  /** Conjunction to use when displaying multiple options ("and" or "or"). Defaults to "or" */
  conjunction?: "and" | "or";
}

/**
 * Generic filter pill component that renders selected filter options
 * @param category - The filter category name
 * @param selectedOptions - Array of selected option values
 * @param onClear - Optional clear handler
 * @param countOnly - If true, always show count with tooltip
 * @param isLoading - If true, shows a skeleton in place of selected options
 * @param menuItems - Array of menu items to display when gear icon is clicked
 * @param conjunction - Conjunction to use when displaying multiple options ("and" or "or")
 */
const GenericFilterPill = ({
  category,
  selectedOptions,
  onClear,
  countOnly = false,
  isLoading = false,
  menuItems,
  conjunction = "or",
}: GenericFilterPillProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);
  const validOptions = selectedOptions.filter(Boolean);
  const hasMenuItems = menuItems && menuItems.length > 0;
  const showGearIcon = hasMenuItems;

  // Reset hover state when menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      // Small delay to allow menu interaction to complete
      const timeoutId = setTimeout(() => {
        if (pillRef.current && !pillRef.current.matches(":hover")) {
          setIsHovered(false);
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isMenuOpen]);

  const handleClearClick = useCallback(() => {
    onClear?.();
  }, [onClear]);

  const displayTitle = useMemo(() => getPrettyName(category), [category]);

  // Check if any option has more than 3 words
  const hasLongLabel = useMemo(() => {
    return validOptions.some((option) => option.trim().split(/\s+/).length > 3);
  }, [validOptions]);

  const displayOptions = useMemo(() => {
    if (countOnly || validOptions.length > 3 || hasLongLabel) {
      return `${validOptions.length} selected`;
    }
    if (validOptions.length === 1) {
      return validOptions[0];
    }
    if (validOptions.length === 2) {
      return `${validOptions[0]} ${conjunction.toUpperCase()} ${validOptions[1]}`;
    }
    if (validOptions.length === 3) {
      return `${validOptions[0]}, ${validOptions[1]} ${conjunction.toUpperCase()} ${validOptions[2]}`;
    }
    return validOptions.join(", ");
  }, [validOptions, countOnly, conjunction, hasLongLabel]);

  const showTooltip = countOnly || validOptions.length > 3 || hasLongLabel;
  const hasConjunction =
    !countOnly &&
    !hasLongLabel &&
    validOptions.length >= 2 &&
    validOptions.length <= 3;

  // Wrap menu items to close menu after onClick - memoized to avoid recreation
  const menuItemsWithClose = useMemo(
    () =>
      menuItems?.map((item) => ({
        ...item,
        onClick: () => {
          item.onClick?.();
          setIsMenuOpen(false);
        },
      })),
    [menuItems],
  );

  const gearIcon = useMemo(() => {
    if (!showGearIcon) return null;

    return (
      <div
        className={`inline-flex items-center overflow-hidden will-change-[width,opacity] transition-all duration-300 ease-in-out ${
          isHovered || isMenuOpen
            ? "opacity-100 h-[28px] w-[28px] translate-x-0"
            : "opacity-0 w-0 -translate-x-2"
        }`}
      >
        <MenuTrigger onOpenChange={setIsMenuOpen}>
          <Button
            size="none"
            variant="ghost"
            className="flex items-center justify-center transition-all duration-300 ease-in-out hover:bg-transparent h-[28px] w-[28px] px-1"
            aria-label="Filter pill settings button"
          >
            <span className="flex items-center justify-center border border-transparent rounded-sm">
              <GearIcon
                className={`text-gray-600 hover:text-gray-900 transition-all duration-300 ease-in-out ${
                  isHovered || isMenuOpen
                    ? "scale-100 rotate-0"
                    : "scale-90 rotate-180"
                }`}
                size={14}
              />
            </span>
          </Button>
          {hasMenuItems && (
            <MenuPopover className="min-w-[100px]">
              <Menu>
                {menuItemsWithClose?.map((item) => (
                  <CustomMenuItem key={String(item.label)} config={item} />
                ))}
              </Menu>
            </MenuPopover>
          )}
        </MenuTrigger>
      </div>
    );
  }, [showGearIcon, isHovered, isMenuOpen, hasMenuItems, menuItemsWithClose]);

  if (isLoading) {
    return (
      <div
        role="status"
        className="inline-flex items-center leading-tight h-[30px] rounded-3xl border border-gray-200 bg-white text-slate-800 text-xs pl-3 pr-3 py-[7px] gap-x-2"
        aria-label={`Loading filter pill for ${displayTitle}`}
      >
        <span className="font-normal shrink-0 text-nowrap text-gray-600">
          {displayTitle}:
        </span>
        <Skeleton className="h-4 w-24 rounded-sm" animate="pulse" />
      </div>
    );
  }

  // Don't render if no valid options
  if (validOptions.length === 0) return null;

  return (
    <div className="relative">
      {/** biome-ignore lint/a11y/noStaticElementInteractions: hover-only to reveal settings gear; not a primary interactive control */}
      {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: names the whole filter pill; role would duplicate nested semantics */}
      <div
        ref={pillRef}
        data-filter-pill={validOptions.length}
        className="inline-flex items-center leading-tight h-[30px] rounded-3xl border border-gray-200 text-slate-800 text-xs pl-3 py-[7px] gap-x-1 transition-all duration-300"
        aria-label={`Filter pill for ${displayTitle}${validOptions.length ? `, ${validOptions.length} selected` : ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          if (!isMenuOpen) {
            setIsHovered(false);
          }
        }}
      >
        <div className="inline-flex items-center gap-x-2 max-w-96 overflow-x-hidden">
          {/* Title */}
          {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: augments visible category text for assistive tech */}
          <span
            className="font-normal shrink-0 text-nowrap"
            aria-label={`Filter pill title: ${displayTitle}`}
          >
            {displayTitle}:
          </span>

          {/* Message */}
          {showTooltip ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: TooltipTrigger child; label describes summary/count text */}
                  <span
                    className="first-letter:uppercase whitespace-nowrap font-semibold text-nowrap overflow-x-hidden cursor-pointer"
                    aria-label={`Filter pill selected options: ${displayOptions}`}
                  >
                    {displayOptions}
                  </span>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent className="max-w-md text-wrap z-100">
                    {validOptions.join(", ")}
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          ) : (
            // biome-ignore lint/a11y/useAriaPropsSupportedByRole: augments visible summary/count text for assistive tech
            <span
              className={`first-letter:uppercase font-semibold overflow-x-hidden ${
                hasConjunction ? "" : "whitespace-nowrap text-nowrap"
              }`}
              aria-label={`Filter pill selected options: ${displayOptions}`}
            >
              {displayOptions}
            </span>
          )}
        </div>

        {(onClear || showGearIcon) && (
          <div className="inline-flex items-center gap-x-1 rounded-r-3xl">
            {/* Gear Icon - shows on hover if menuItems exist */}
            {gearIcon}

            {/* Clear Button */}
            {onClear && (
              <span>
                <Button
                  size="none"
                  variant="ghost"
                  onPress={handleClearClick}
                  className="flex items-center transition duration-300 rounded-r-3xl hover:bg-transparent h-[28px] pl-1 pr-2"
                  aria-label="Filter pill clear button"
                >
                  <span className="flex items-center justify-center border border-transparent rounded-sm">
                    <XIcon
                      className="text-gray-600 hover:text-gray-900 transition duration-300"
                      size={14}
                    />
                  </span>
                </Button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(GenericFilterPill);
